// Retailer Details bottom sheet — full outlet profile + recent transaction
// history, opened from an "Info" button next to Start Call so an SE can
// check who they're visiting before committing to a geo-fenced check-in.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Phone, MapPin, Camera, Building2, Route as RouteIcon, ShoppingBag, BarChart3 } from "lucide-react";
import { api, API_BASE, getToken, formatApiError, photoRefPath } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isOnline } from "@/offline/apiClient";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

// GET /api/photos/{ref} requires a bearer token, which a plain <img src> can't
// send — fetch the bytes ourselves and hand the <img> a local object URL.
async function fetchPhotoObjectUrl(ref) {
  if (!ref) return null;
  if (ref.startsWith("http") || ref.startsWith("blob:")) return ref;
  const res = await fetch(`${API_BASE}/photos/${photoRefPath(ref)}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error(`photo fetch failed: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// Same capture/compress/upload pattern as AddOutlet.jsx's storefront photo.
async function compressJpeg(file, maxDim = 1280, quality = 0.75) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = URL.createObjectURL(file);
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  return await new Promise((r) => canvas.toBlob(r, "image/jpeg", quality));
}

async function uploadPhoto(blob) {
  const fd = new FormData();
  fd.append("file", blob, `storefront-${Date.now()}.jpg`);
  fd.append("context", "outlet_storefront");
  const res = await fetch(`${API_BASE}/photos/upload`, {
    method: "POST", body: fd,
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error(`upload failed: ${res.status}`);
  return await res.json();
}

export default function RetailerDetailsSheet({ retailerId, open, onOpenChange }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [beatName, setBeatName] = useState(null);
  const [distributorName, setDistributorName] = useState(null);
  const [lastOrder, setLastOrder] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoSrc, setPhotoSrc] = useState(null);

  // Load the storefront photo as an authenticated blob URL whenever the ref
  // changes (freshly fetched profile, or right after a new photo is saved).
  useEffect(() => {
    const ref = profile?.storefront_photo_ref;
    if (!ref) { setPhotoSrc(null); return; }
    let objectUrl = null;
    let cancelled = false;
    fetchPhotoObjectUrl(ref).then((url) => {
      if (cancelled) return;
      objectUrl = url;
      setPhotoSrc(url);
    }).catch(() => { if (!cancelled) setPhotoSrc(null); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [profile?.storefront_photo_ref]);

  useEffect(() => {
    if (!open || !retailerId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setProfile(null);
    setBeatName(null);
    setDistributorName(null);
    setLastOrder(null);
    setRecentOrders([]);

    (async () => {
      try {
        const [{ data: r }, lastOrderRes, historyRes] = await Promise.all([
          api.get(`/retailers/${retailerId}`),
          api.get(`/retailers/${retailerId}/last-order`).catch(() => ({ data: { order: null } })),
          // order-history (not the admin /orders grid) so each row carries
          // per-line SKU names, not just qty/uom chips.
          api.get(`/retailers/${retailerId}/order-history`, { params: { months: 12 } })
            .catch(() => ({ data: { orders: [] } })),
        ]);
        if (cancelled) return;
        setProfile(r);
        setLastOrder(lastOrderRes.data?.order || null);
        setRecentOrders((historyRes.data?.orders || []).slice(0, 6));

        const lookups = [];
        if (r.beat_id) {
          lookups.push(
            api.get("/beats", { params: { ids: r.beat_id, page_size: 1 } })
              .then(({ data }) => { if (!cancelled) setBeatName(data.items?.[0]?.name || null); })
              .catch(() => {})
          );
        }
        if (r.distributor_id) {
          lookups.push(
            api.get("/distributors", { params: { ids: r.distributor_id, page_size: 1 } })
              .then(({ data }) => { if (!cancelled) setDistributorName(data.items?.[0]?.name || null); })
              .catch(() => {})
          );
        }
        await Promise.all(lookups);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, retailerId]);

  const photo = photoSrc;

  const capturePhoto = () => {
    if (!isOnline()) {
      toast.error("You're offline — try capturing the shop photo again once connected.");
      return;
    }
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*"; input.capture = "environment";
    input.onchange = async () => {
      const f = input.files?.[0]; if (!f) return;
      setPhotoBusy(true);
      try {
        const small = await compressJpeg(f);
        const { ref } = await uploadPhoto(small);
        const meta = { captured_at_ts: new Date().toISOString(), uploader_user_id: user?.id };
        const { data } = await api.patch(`/retailers/${retailerId}/storefront-photo`, {
          storefront_photo_ref: ref, storefront_photo_meta: meta,
        });
        setProfile(data);
        toast.success("Shop photo saved");
      } catch (e) {
        toast.error(formatApiError(e) || "Photo capture failed");
      } finally {
        setPhotoBusy(false);
      }
    };
    input.click();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[88vh] overflow-y-auto rounded-t-2xl p-0"
                    data-testid="retailer-details-sheet">
        <SheetHeader className="p-4 pb-2 text-left">
          <SheetTitle className="text-lg">{profile?.name || "Retailer details"}</SheetTitle>
          <SheetDescription>
            {profile?.code ? <span className="font-mono">{profile.code}</span> : "Loading outlet profile…"}
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 pb-6 space-y-4">
          {loading && !profile && (
            <div className="flex items-center gap-2 text-sm text-neutral-500 py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {error && <div className="text-sm text-red-600">{error}</div>}

          {profile && (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-neutral-100 text-neutral-700">{profile.channel_type || "Outlet"}</Badge>
                  <Badge className={profile.is_active === false
                    ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-800"}>
                    {profile.is_active === false ? "Inactive" : "Active"}
                  </Badge>
                </div>
                <Button size="sm" variant="outline"
                        onClick={() => { onOpenChange(false); nav(`/field/retailer/${retailerId}/dashboard`); }}
                        className="h-7 px-2 text-xs"
                        data-testid="retailer-details-full-dashboard">
                  <BarChart3 className="h-3.5 w-3.5 mr-1" /> Full history
                </Button>
              </div>

              <div className="rounded-xl border border-neutral-200 divide-y divide-neutral-100">
                <DetailRow label="Owner" value={profile.owner_name} />
                <DetailRow label="Phone" value={profile.phone && (
                  <a href={`tel:${profile.phone}`} className="text-[#6D2C91] font-semibold flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" /> {profile.phone}
                  </a>
                )} />
                <DetailRow label="Address" value={profile.address} icon={MapPin} />
                <DetailRow label="Beat" value={beatName || profile.beat_name || "—"} icon={RouteIcon} />
                <DetailRow label="Distributor" value={distributorName || profile.distributor_name || "—"} icon={Building2} />
                <DetailRow label="GPS" value={
                  profile.gps_lat && profile.gps_lng ? (
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${profile.gps_lat},${profile.gps_lng}`}
                       target="_blank" rel="noopener noreferrer"
                       className="text-[#6D2C91] font-semibold flex items-center gap-1"
                       data-testid="retailer-details-navigate">
                      <MapPin className="h-3.5 w-3.5" />
                      {profile.gps_lat.toFixed(5)}, {profile.gps_lng.toFixed(5)} — Navigate
                    </a>
                  ) : <span className="text-neutral-400 italic">not geo-tagged</span>
                } />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[11px] uppercase tracking-widest text-neutral-400 flex items-center gap-1">
                    <Camera className="h-3 w-3" /> Storefront
                  </div>
                  <Button size="sm" variant="outline" onClick={capturePhoto} disabled={photoBusy}
                          className="h-7 px-2 text-xs" data-testid="retailer-details-capture-photo">
                    {photoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Camera className="h-3.5 w-3.5 mr-1" />}
                    {photo ? "Retake" : "Add photo"}
                  </Button>
                </div>
                {photo ? (
                  <div className="rounded-xl overflow-hidden bg-neutral-100 border border-neutral-200 aspect-[4/3]"
                       data-testid="retailer-details-photo">
                    <img src={photo} alt="storefront" className="w-full h-full object-cover"
                         onError={(e) => { e.target.style.display = "none"; }} />
                  </div>
                ) : (
                  <div className="rounded-xl border-2 border-dashed border-neutral-200 py-6 text-center text-neutral-400 text-xs">
                    No storefront photo captured
                  </div>
                )}
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-widest text-neutral-400 mb-1.5 flex items-center gap-1">
                  <ShoppingBag className="h-3 w-3" /> Last order — SKU breakdown
                </div>
                {lastOrder ? (
                  <div className="rounded-xl border border-neutral-200 p-3 space-y-1.5"
                       data-testid="retailer-details-last-order">
                    <div className="flex items-center justify-between text-xs text-neutral-500">
                      <span className="font-mono">{lastOrder.order_no}</span>
                      <span>{lastOrder.booking_ts ? new Date(lastOrder.booking_ts).toLocaleDateString([], { dateStyle: "medium" }) : ""}</span>
                    </div>
                    {(lastOrder.top_5_lines || []).map((l) => (
                      <div key={l.id} className="flex items-center justify-between text-sm">
                        <span className="text-neutral-800 truncate mr-2">{l.sku_name_snapshot || l.sku_code_snapshot}</span>
                        <span className="font-mono text-neutral-500 shrink-0">
                          {l.qty_kg ? `${l.qty_kg} kg` : `${l.qty_pcs ?? l.qty ?? 0} pcs`}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between text-sm font-bold pt-1 border-t border-neutral-100">
                      <span>Net</span><span className="text-[#E5197F]">₹{lastOrder.net_amount}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-neutral-400 italic">No past orders yet.</div>
                )}
              </div>

              {recentOrders.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-neutral-400 mb-1.5">
                    Recent transactions
                  </div>
                  <div className="rounded-xl border border-neutral-200 divide-y divide-neutral-100">
                    {recentOrders.map((o) => {
                      const skuNames = (o.lines || []).map((l) => l.sku_name || l.sku_code).filter(Boolean);
                      const skuLabel = skuNames.length > 2
                        ? `${skuNames.slice(0, 2).join(", ")} +${skuNames.length - 2} more`
                        : skuNames.join(", ");
                      return (
                        <div key={o.order_id} className="flex items-center justify-between px-3 py-2 text-sm"
                             data-testid={`retailer-details-order-${o.order_no}`}>
                          <div className="min-w-0 pr-2">
                            <div className="font-mono text-[11px] text-neutral-400">{o.order_no}</div>
                            <div className="text-xs text-neutral-500">
                              {o.order_date ? new Date(o.order_date).toLocaleDateString([], { dateStyle: "medium" }) : "—"}
                            </div>
                            {skuLabel && (
                              <div className="text-xs text-neutral-700 truncate">{skuLabel}</div>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-semibold text-neutral-900">
                              {o.total_qty_kg ? `${Number(o.total_qty_kg).toFixed(1)} kg` : "—"}
                            </div>
                            <div className="text-[10px] uppercase text-neutral-400">{o.status}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DetailRow({ label, value, icon: Icon }) {
  return (
    <div className="flex items-start gap-3 px-3 py-2 text-sm">
      <span className="w-20 shrink-0 text-neutral-500 flex items-center gap-1">
        {Icon && <Icon className="h-3.5 w-3.5 text-neutral-400" />}{label}
      </span>
      <span className="flex-1 min-w-0 text-neutral-900 break-words">{value ?? "—"}</span>
    </div>
  );
}
